import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
    Label,
    LabelList,
} from "recharts";
import { Layers } from "lucide-react";

const NaicsTrend = ({ data }) => {
    if (!data || data.length === 0) return null;

    // Helper for simple formatting
    const kFormat = (val) => `${(val / 1000).toFixed(0)}k`;

    return (
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 h-full flex flex-col overflow-hidden">
            <div className="d-flex align-items-center justify-content-between mb-6">
                <div className="d-flex align-items-center gap-2">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                        <Layers size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 m-0">
                        Workforce Growth by Sector
                    </h3>
                </div>
                <div className="text-[10px] font-bold text-blue-500 bg-blue-50 px-3 py-1 rounded-full uppercase tracking-wider border border-blue-100">
                    Employment Trends
                </div>
            </div>

            <div className="flex-grow w-full" style={{ height: 350, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 30, right: 30, left: 10, bottom: 40 }}
                        barGap={0}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                            dataKey="year"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            dy={10}
                        >
                            <Label
                                value="Calendar Year"
                                offset={-15}
                                position="insideBottom"
                                style={{ fontSize: '11px', fill: '#94a3b8', fontWeight: 600 }}
                            />
                        </XAxis>
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            tickFormatter={kFormat}
                        >
                            <Label
                                value="Total Jobs"
                                angle={-90}
                                position="insideLeft"
                                style={{ textAnchor: 'middle', fontSize: '11px', fill: '#94a3b8', fontWeight: 600 }}
                                offset={0}
                            />
                        </YAxis>
                        <Tooltip
                            cursor={{ fill: '#f8fafc' }}
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                            formatter={(val, name) => [`${val.toLocaleString()} Jobs`, name]}
                        />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            iconType="circle"
                            wrapperStyle={{ fontSize: '11px', paddingBottom: '30px' }}
                        />

                        {/* Stacked Bars for each NAICS sector */}
                        <Bar
                            name="Residential (236)"
                            dataKey="naics_236"
                            stackId="a"
                            fill="#3b82f6"
                            radius={[0, 0, 0, 0]}
                        />
                        <Bar
                            name="Infrastructure (237)"
                            dataKey="naics_237"
                            stackId="a"
                            fill="#06b6d4"
                            radius={[0, 0, 0, 0]}
                        />
                        <Bar
                            name="Specialty Trades (238)"
                            dataKey="naics_238"
                            stackId="a"
                            fill="#8b5cf6"
                            radius={[6, 6, 0, 0]}
                        >
                            <LabelList
                                dataKey="total"
                                position="top"
                                formatter={kFormat}
                                style={{ fontSize: '11px', fill: '#1e293b', fontWeight: 'bold' }}
                                offset={12}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 d-flex align-items-center justify-content-between text-[11px] text-gray-400 font-medium italic">
                <div className="d-flex align-items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                    <span>Steady upward growth in Specialty Trades</span>
                </div>
                <span className="font-semibold text-gray-600">
                    Total: {data && data.length > 0 && data[data.length - 1].total ? kFormat(data[data.length - 1].total) : "N/A"}
                </span>
            </div>
        </div>
    );
};

export default NaicsTrend;
